import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { Country } from '../data/countries';
import {
  Address,
  Card,
  PersonalData,
  ShoppingCart,
  ShoppingCartV2,
} from './checkoutTypes';
import {
  addDiscountCode,
  addDiscountCodeV2,
  checkout,
  createVerification,
  verifyCode,
} from './checkoutActions';
import { api } from '../api/api';

export const CHECKOUT_DATA_KEY = 'checkoutData';

export enum Step {
  CreateAccount,

  LastName,
  BirthDate,
  Gender,
  Country,
  SelectCountry,

  Avatar,
  PaymentInformation,
  BillingAddress,
  DeliveryAddress,
  Summary,

  PhoneNumber,
  PhoneNumberCountry,
  VerificationCode,
}

export type NavigationState =
  | 'LOGGED_OUT'
  | 'SHOW_SUCCESS_ONBOARDING'
  | 'LOGGED_IN';

interface State {
  buttonEnabled: boolean;
  loading: boolean;
  step: Step;
  phoneCodeCountry: Country;
  country: Country;

  personalData: PersonalData;
  coupon: string;

  cart?: ShoppingCart;
  cartV2?: ShoppingCartV2;

  billingAddress?: Address;
  deliveryAddress?: Address;

  card?: Card;
  navigationState?: NavigationState;
  hasInitialisedCountry: boolean;
}

const dummyCountryGB: Country = {
  code: 'GB',
  label: 'United Kingdom',
  phone: '44',
  flag: 'data:image/png;base64,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',
};

const dummyCountryJA: Country = {
  code: 'JP',
  label: '日本',
  phone: '81',
  flag: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACgAAAAeCAMAAABpA6zvAAAABGdBTUEAALGPC/xhBQAAAAFzUkdCAK7OHOkAAAAgY0hSTQAAeiYAAICEAAD6AAAAgOgAAHUwAADqYAAAOpgAABdwnLpRPAAAAGlQTFRF//////7+//z8/vz8/PDw9cfH8a6u98/P4lpa1hIS0gAA0QAA41pa//396Hp60gEB0AAA0wEB1AMDzwAA0wQE1AQE0wAA41tb1AYG9svL0gMD0wIC4l1d/O/v1hcX9cjI8a+v6Ht799DQeS2ujAAAAAFiS0dEAIgFHUgAAAAJcEhZcwAAAEgAAABIAEbJaz4AAADRSURBVDjLzZPZDoMgEEUH0FrBBdz37f8/sqBJqynqPDW9LyTk5A7M3AH4IxHKGCW3GGXbyei1m8Yc9/FwHY2SS857+lwI7gfeBUlCiGLJVZIoLuMIwjOSQJBmuSq0VJ6lJZyADKK0WrEVrdIImI0LoW7aN6fJtqn1pc2wk3mxUy47myWBfuBqDyo+9JZnUhiPnCFHoJbKk0iOYCImS200iC6N/gy6PfiGf49wto/QPLNEhcLEbP7EbD6PmQnuEmzBLZe7iKNWAdDLtbri1vWHegEoyhjkyVR7IwAAACV0RVh0ZGF0ZTpjcmVhdGUAMjAxMy0xMC0wN1QxMzoxNDoyNSswMjowMIkrI+8AAAAldEVYdGRhdGU6bW9kaWZ5ADIwMTMtMTAtMDdUMTM6MTQ6MjUrMDI6MDD4dptTAAAAAElFTkSuQmCC',
};

const initialState: State = {
  buttonEnabled: false,
  loading: false,
  step: Step.CreateAccount,
  phoneCodeCountry: dummyCountryGB,
  country: dummyCountryGB,
  hasInitialisedCountry: false,
  navigationState: undefined,
  coupon: '',

  personalData: {
    name: '',
    lastName: '',
    email: '',
    password: '',
    phoneNumber: '',
    verificationId: -1,
    birthDate: '',
    gender: '',
    avatar: '',
    avatarId: '',
  },
};

export const NAVIGATION_STATE_KEY = 'navigationState';
export const CONTINUE_HERE_SELECTED = 'continueHereSelected';

export const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    setButtonState(state, action: PayloadAction<boolean>) {
      state.buttonEnabled = action.payload;
    },
    setStep(state, action: PayloadAction<Step>) {
      state.step = action.payload;
    },
    setPhoneCodeCountry(state, action: PayloadAction<Country>) {
      state.phoneCodeCountry = action.payload;
    },
    setCountry(state, action: PayloadAction<Country>) {
      state.country = action.payload;
    },
    setPersonalData(state, action: PayloadAction<Partial<PersonalData>>) {
      state.personalData = {
        ...state.personalData,
        ...action.payload,
      };
    },
    setBillingAddress(state, action: PayloadAction<Address>) {
      state.billingAddress = action.payload;
    },
    setDeliveryAddress(state, action: PayloadAction<Address>) {
      state.deliveryAddress = action.payload;
    },
    setCard(state, action: PayloadAction<Card>) {
      state.card = action.payload;
    },
    setNavigationState(state, action: PayloadAction<NavigationState>) {
      state.navigationState = action.payload;
    },
    setButtonLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setCheckoutState(state, action: PayloadAction<State>) {
      state.buttonEnabled = action.payload.buttonEnabled;
      state.loading = action.payload.loading;
      state.step = action.payload.step;
      state.country = action.payload.country;
      state.coupon = action.payload.coupon;

      state.personalData = action.payload.personalData;
      state.cart = action.payload.cart;
      state.cartV2 = action.payload.cartV2;
    },
    updateInitialCountry(state, action: PayloadAction<string>) {
      state.hasInitialisedCountry = true;

      if (action.payload === 'ja') {
        state.country = dummyCountryJA;
        state.phoneCodeCountry = dummyCountryJA;
      } else {
        state.country = dummyCountryGB;
        state.phoneCodeCountry = dummyCountryGB;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addDiscountCodeV2.fulfilled, (state, action) => {
      state.cartV2 = action.payload;
      state.coupon = action.meta.arg;
    });

    builder.addCase(addDiscountCode.fulfilled, (state, action) => {
      state.cart = action.payload;
    });

    builder.addCase(createVerification.fulfilled, (state, action) => {
      state.personalData.verificationId = action.payload.verificationId;
    });

    builder.addMatcher(
      isAnyOf(
        addDiscountCode.pending,
        createVerification.pending,
        verifyCode.pending,
        api.endpoints.updatePhoneNumber.matchPending,
        api.endpoints.emailAvailable.matchPending,
        checkout.pending,
        addDiscountCodeV2.pending
      ),
      (state) => {
        state.loading = true;
      }
    );

    builder.addMatcher(
      isAnyOf(
        addDiscountCode.fulfilled,
        addDiscountCode.rejected,
        addDiscountCodeV2.fulfilled,
        addDiscountCodeV2.rejected,
        createVerification.fulfilled,
        createVerification.rejected,
        api.endpoints.updatePhoneNumber.matchFulfilled,
        api.endpoints.emailAvailable.matchFulfilled,
        checkout.fulfilled
      ),
      (state) => {
        state.loading = false;
      }
    );
  },
});

export const checkoutActions = checkoutSlice.actions;

export type CheckoutState = State;

export default checkoutSlice.reducer;
